import type { TimelineFishGroup, TimelineOperation } from '@/domain/fishGroups';

import type { ChainLink, ChainMember, GroupRowModel, RowRole, WeekAxis } from '../types';

import { badgeForOperation, buildGroupTrack, endedKindOf, type GroupTrack } from './groupTrack';
import { layoutTrackLabels, TRACK_MIN_WIDTH_PX } from './trackLabels';
import { groupStart, positionOf } from './weekAxis';

export interface ChainRowContext {
    axis: WeekAxis;
    now: Date;
    role: RowRole;
    originCount: number;
    /** The operation that created the chain's first group from in-scope sources, if any. */
    creatingOperation: TimelineOperation | null;
    /** The other groups in an operation, by display name (a redistribution's badge names them). */
    peersOf: (operationId: number, groupId: number) => string[];
}

function memberOf(group: TimelineFishGroup): ChainMember {
    return {
        groupId: group.id,
        name: group.name,
        isLive: group.isLive,
        weight: group.weight,
        endedKind: endedKindOf(group),
    };
}

/** A member's labels on a chain row: keyed apart from the other members', and owned by it. */
function ownedBy<T extends { key: string }>(items: T[], groupId: number): T[] {
    return items.map((item) => ({ ...item, key: `${groupId}-${item.key}`, groupId }));
}

/**
 * Every group's track on one row. The operation that closed an earlier group is the link to
 * the next one, drawn as a tick rather than its badge; the next group's first pen tag would
 * repeat the pen, so it goes too.
 */
function chainTrack(
    chain: TimelineFishGroup[],
    context: ChainRowContext
): GroupTrack & { links: ChainLink[] } {
    const { axis, now, peersOf } = context;
    const tracks = chain.map((group, index) =>
        buildGroupTrack(
            index === chain.length - 1 ? group : { ...group, endedBy: null },
            axis,
            now,
            group.parentIds.length > 0,
            (operationId) => peersOf(operationId, group.id)
        )
    );
    const last = tracks[tracks.length - 1];
    if (tracks.length === 1) return { ...last, links: [] };

    // A group's first stay carries on the stay before it in the same pen, whose tag names both:
    // keyed as one run, that tag fits against the whole run and the run's first bar names it.
    const runOf = new Map<string, string>();
    chain.slice(1).forEach((group, index) => {
        const previous = chain[index].stays[chain[index].stays.length - 1];
        const first = group.stays[0];
        if (!previous || !first) return;
        const previousKey = String(previous.id);
        runOf.set(String(first.id), runOf.get(previousKey) ?? previousKey);
    });
    const segments = tracks.flatMap((track, index) =>
        track.segments.map((segment, at, all) => ({
            ...segment,
            stayKey: runOf.get(segment.stayKey) ?? segment.stayKey,
            groupId: chain[index].id,
            joinsPrevious: index > 0 && at === 0,
            joinsNext: index < chain.length - 1 && at === all.length - 1,
        }))
    );
    const links = chain.slice(1).flatMap((group, index): ChainLink[] => {
        const date = groupStart(group);
        if (!date) return [];
        return [
            {
                key: `link-${group.id}`,
                leftPct: positionOf(axis, date),
                date,
                fromName: chain[index].name,
                toName: group.name,
                kind: badgeForOperation(chain[index].endedBy),
                groupId: group.id,
            },
        ];
    });
    const merged = layoutTrackLabels(
        {
            ...last,
            segments,
            penTags: tracks.flatMap((track, index) =>
                (index === 0 ? track.penTags : track.penTags.slice(1)).map((tag) => ({
                    ...tag,
                    groupId: chain[index].id,
                }))
            ),
            badges: tracks.flatMap((track, index) => ownedBy(track.badges, chain[index].id)),
            markers: tracks.flatMap((track, index) => ownedBy(track.markers, chain[index].id)),
            ticks: tracks.flatMap((track, index) => ownedBy(track.ticks, chain[index].id)),
        },
        TRACK_MIN_WIDTH_PX
    );
    return { ...merged, links };
}

/** One board row for a chain of groups (oldest first), named and summed up by the latest. */
export function buildChainRow(chain: TimelineFishGroup[], context: ChainRowContext): GroupRowModel {
    const { axis, role, originCount, creatingOperation, peersOf } = context;
    const first = chain[0];
    const latest = chain[chain.length - 1];
    const creationKind = badgeForOperation(creatingOperation);
    const start = groupStart(first);

    return {
        groupId: latest.id,
        name: latest.name,
        isLive: latest.isLive,
        role,
        originCount,
        members: chain.map(memberOf),
        creationBadge:
            creationKind && start
                ? {
                      key: `created-${first.id}`,
                      groupId: first.id,
                      leftPct: positionOf(axis, start),
                      kinds: [creationKind],
                      labelShown: true,
                      peers:
                          creationKind === 'redistribution' && creatingOperation
                              ? peersOf(creatingOperation.id, first.id)
                              : [],
                  }
                : null,
        ...chainTrack(chain, context),
    };
}
